import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { format } from "date-fns";

import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Card, CardContent } from "./components/ui/card";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";

import {
  Phone, MessageCircle, MapPin, ChevronRight, CheckCircle2, Star,
  Users, Wallet, Shield, Clock, ArrowRight, CalendarIcon, Search,
  Zap, Wrench, Fan, Paintbrush, Bug, Sparkles, Hammer, Blocks,
  Smartphone, Laptop, Tv, Battery, Refrigerator, Circle, LayoutGrid,
  Lock, GripHorizontal, Armchair, DoorOpen, Square, Flame, FileText,
  Building2, Briefcase, Scale, Palette, Ruler, Compass, Home, Cpu, Printer
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "919502299229";
const PHONE_NUMBER = "9502299229";
const LOCATIONS = [
  "All Places",
  "Anantapur",
  "Hindupur",
  "Kadiri",
  "Guntakal",
  "Tadipatri",
  "Dharmavaram",
  "Penukonda",
  "Rayadurg",
  "Kalyandurg",
  "Gooty"
];

function Header() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header data-testid="main-header" className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" data-testid="logo" className="flex items-center gap-1">
            <span className="logo-99 text-2xl md:text-3xl">99</span>
            <span className="text-xl md:text-2xl font-bold text-slate-800">Home</span>
            <span className="logo-deals text-xl md:text-2xl">Deals</span>
          </a>
          <a href={`tel:${PHONE_NUMBER}`} data-testid="header-call-btn" className="hidden md:flex items-center gap-2 bg-blue-800 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-blue-900 transition-all shadow-lg hover:shadow-blue-900/20">
            <Phone className="w-4 h-4" />
            <span>Call Now</span>
          </a>
        </div>
      </div>
    </header>
  );
}

function HeroSection({ onBookNow }) {
  return (
    <section data-testid="hero-section" className="gradient-hero pt-24 md:pt-32 pb-16 md:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="space-y-6 md:space-y-8">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold border border-blue-100">
              <Shield className="w-4 h-4" />
              <span>Trusted by 10,000+ Homes</span>
            </div>
            <h1 data-testid="hero-headline" className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              మీ ఇంటి పనులన్నీ <span className="text-blue-800">ఒకే ప్లాట్‌ఫామ్‌లో</span>
            </h1>
            <p data-testid="hero-subheadline" className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Expert Home Services at Your Doorstep. <span className="font-bold text-slate-800">Book any service at just <span className="text-2xl text-yellow-500">₹99/-</span> confirmed visit.</span>
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="trust-badge flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-slate-700 shadow-sm border border-slate-100">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Verified Professionals</span>
              </div>
              <div className="trust-badge flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-slate-700 shadow-sm border border-slate-100">
                <Clock className="w-4 h-4 text-green-600" />
                <span>On-Time Guarantee</span>
              </div>
              <div className="trust-badge flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-slate-700 shadow-sm border border-slate-100">
                <Shield className="w-4 h-4 text-green-600" />
                <span>Transparent Pricing</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button data-testid="hero-book-now-btn" onClick={onBookNow} className="cta-btn-primary bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-yellow-400/30 transition-all">
                Book Service Now <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I need a home service`} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn">
                <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 font-semibold px-8 py-6 text-lg rounded-full w-full">
                  <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
          <div className="hero-image-grid relative">
            <img src="https://images.pexels.com/photos/7220892/pexels-photo-7220892.jpeg?auto=compress&cs=tinysrgb&w=600" alt="Professional Plumber" className="w-full h-48 md:h-64 lg:h-80 object-cover shadow-xl rounded-xl" />
            <img src="https://images.pexels.com/photos/9679179/pexels-photo-9679179.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Electrician Working" className="w-full h-36 md:h-48 object-cover shadow-lg rounded-xl" />
            <img src="https://images.pexels.com/photos/5463580/pexels-photo-5463580.jpeg?auto=compress&cs=tinysrgb&w=400" alt="AC Technician" className="w-full h-36 md:h-48 object-cover shadow-lg rounded-xl" />
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white px-6 py-3 rounded-full shadow-xl border-2 border-yellow-400">
              <span className="text-2xl font-bold text-slate-900">Starting @ <span className="text-yellow-500 text-3xl">₹99</span></span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeCareServices({ onSelect }) {
  const services = [
    { id: "electrical", name: "Electrical", Icon: Zap },
    { id: "plumbing", name: "Plumbing", Icon: Wrench },
    { id: "ac_repair", name: "AC Repair", Icon: Fan },
    { id: "painting", name: "Painting", Icon: Paintbrush },
    { id: "pest_control", name: "Pest Control", Icon: Bug },
    { id: "cleaning", name: "Cleaning", Icon: Sparkles },
    { id: "carpentry", name: "Carpentry", Icon: Hammer },
    { id: "masonry", name: "Masonry", Icon: Blocks },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {services.map((s) => (
        <Card key={s.id} data-testid={`service-card-${s.id}`} onClick={() => onSelect(s)} className="service-card bg-white border border-slate-100 hover:border-blue-200 cursor-pointer group">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <s.Icon className="w-6 h-6 text-blue-800" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 group-hover:text-blue-800 transition-colors">{s.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="price-badge text-slate-900">₹99</span>
                  <span className="text-xs text-slate-500">visit charge</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-800 group-hover:translate-x-1 transition-all" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TechRepairsServices({ onSelect }) {
  const services = [
    { id: "mobile_repair", name: "Mobile Repair", Icon: Smartphone },
    { id: "laptop_repair", name: "Laptop Repair", Icon: Laptop },
    { id: "tv_repair", name: "TV Repair", Icon: Tv },
    { id: "inverter_repair", name: "Inverter Repair", Icon: Battery },
    { id: "appliance_repair", name: "Appliance Repair", Icon: Refrigerator },
    { id: "washing_machine", name: "Washing Machine", Icon: Circle },
    { id: "microwave_repair", name: "Microwave Repair", Icon: Square },
    { id: "printer_repair", name: "Printer Repair", Icon: Printer },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {services.map((s) => (
        <Card key={s.id} data-testid={`service-card-${s.id}`} onClick={() => onSelect(s)} className="service-card bg-white border border-slate-100 hover:border-blue-200 cursor-pointer group">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <s.Icon className="w-6 h-6 text-blue-800" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 group-hover:text-blue-800 transition-colors">{s.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="price-badge text-slate-900">₹99</span>
                  <span className="text-xs text-slate-500">visit charge</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-800 group-hover:translate-x-1 transition-all" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function HandymanServices({ onSelect }) {
  const services = [
    { id: "drilling", name: "Drilling", Icon: Circle },
    { id: "wall_mounting", name: "Wall Mounting", Icon: LayoutGrid },
    { id: "lock_repair", name: "Lock Repair", Icon: Lock },
    { id: "curtain_rods", name: "Curtain Rods", Icon: GripHorizontal },
    { id: "furniture_assembly", name: "Furniture Assembly", Icon: Armchair },
    { id: "door_repair", name: "Door Repair", Icon: DoorOpen },
    { id: "window_repair", name: "Window Repair", Icon: Square },
    { id: "geyser_install", name: "Geyser Install", Icon: Flame },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {services.map((s) => (
        <Card key={s.id} data-testid={`service-card-${s.id}`} onClick={() => onSelect(s)} className="service-card bg-white border border-slate-100 hover:border-blue-200 cursor-pointer group">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <s.Icon className="w-6 h-6 text-blue-800" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 group-hover:text-blue-800 transition-colors">{s.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="price-badge text-slate-900">₹99</span>
                  <span className="text-xs text-slate-500">visit charge</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-800 group-hover:translate-x-1 transition-all" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ProServices({ onSelect }) {
  const services = [
    { id: "documentation", name: "Documentation", Icon: FileText },
    { id: "real_estate", name: "Real Estate", Icon: Building2 },
    { id: "insurance", name: "Insurance", Icon: Shield },
    { id: "legal_services", name: "Legal Services", Icon: Scale },
    { id: "interior_design", name: "Interior Design", Icon: Palette },
    { id: "architecture", name: "Architecture", Icon: Ruler },
    { id: "vastu", name: "Vastu Consultation", Icon: Compass },
    { id: "home_inspection", name: "Home Inspection", Icon: Search },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {services.map((s) => (
        <Card key={s.id} data-testid={`service-card-${s.id}`} onClick={() => onSelect(s)} className="service-card bg-white border border-slate-100 hover:border-blue-200 cursor-pointer group">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <s.Icon className="w-6 h-6 text-blue-800" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 group-hover:text-blue-800 transition-colors">{s.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="price-badge text-slate-900">₹99</span>
                  <span className="text-xs text-slate-500">visit charge</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-800 group-hover:translate-x-1 transition-all" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ServicesSection({ onServiceSelect }) {
  return (
    <section data-testid="services-section" id="services" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block bg-yellow-100 text-yellow-800 px-4 py-1 rounded-full text-sm font-bold mb-4">OUR SERVICES</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">30+ Services at Your Fingertips</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">From electrical repairs to home documentation - we've got you covered</p>
        </div>
        <Tabs defaultValue="home_care" className="w-full">
          <TabsList data-testid="service-tabs" className="w-full flex flex-wrap justify-center gap-2 mb-8 bg-transparent h-auto">
            <TabsTrigger value="home_care" data-testid="tab-home_care" className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-200 data-[state=active]:bg-blue-800 data-[state=active]:text-white data-[state=active]:border-blue-800 font-semibold transition-all">
              <Home className="w-4 h-4" /><span className="hidden sm:inline">Home Care</span>
            </TabsTrigger>
            <TabsTrigger value="tech_repairs" data-testid="tab-tech_repairs" className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-200 data-[state=active]:bg-blue-800 data-[state=active]:text-white data-[state=active]:border-blue-800 font-semibold transition-all">
              <Cpu className="w-4 h-4" /><span className="hidden sm:inline">Tech Repairs</span>
            </TabsTrigger>
            <TabsTrigger value="handyman" data-testid="tab-handyman" className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-200 data-[state=active]:bg-blue-800 data-[state=active]:text-white data-[state=active]:border-blue-800 font-semibold transition-all">
              <Wrench className="w-4 h-4" /><span className="hidden sm:inline">Handyman</span>
            </TabsTrigger>
            <TabsTrigger value="pro_services" data-testid="tab-pro_services" className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-200 data-[state=active]:bg-blue-800 data-[state=active]:text-white data-[state=active]:border-blue-800 font-semibold transition-all">
              <Briefcase className="w-4 h-4" /><span className="hidden sm:inline">Pro Services</span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="home_care"><HomeCareServices onSelect={onServiceSelect} /></TabsContent>
          <TabsContent value="tech_repairs"><TechRepairsServices onSelect={onServiceSelect} /></TabsContent>
          <TabsContent value="handyman"><HandymanServices onSelect={onServiceSelect} /></TabsContent>
          <TabsContent value="pro_services"><ProServices onSelect={onServiceSelect} /></TabsContent>
        </Tabs>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section data-testid="how-it-works-section" className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-bold mb-4">HOW IT WORKS</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Simple 3-Step Process</h2>
          <p className="text-lg text-slate-600">Book any service in under 2 minutes</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          <div className="hidden md:block absolute top-20 left-1/4 right-1/4 h-1 bg-gradient-to-r from-yellow-400 via-yellow-400 to-slate-200 rounded-full"></div>
          <div data-testid="step-1" className="relative text-center">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-yellow-400 text-slate-900 font-bold flex items-center justify-center text-sm z-10">1</div>
            <div className="w-24 h-24 mx-auto rounded-full bg-white shadow-lg flex items-center justify-center mb-6 border-4 border-slate-100">
              <Search className="w-10 h-10 text-blue-800" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-1">Choose Service</h3>
            <p className="text-blue-800 font-semibold mb-2">సేవ ఎంచుకోండి</p>
            <p className="text-slate-600">Browse 30+ services and select what you need</p>
          </div>
          <div data-testid="step-2" className="relative text-center">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-yellow-400 text-slate-900 font-bold flex items-center justify-center text-sm z-10">2</div>
            <div className="w-24 h-24 mx-auto rounded-full bg-white shadow-lg flex items-center justify-center mb-6 border-4 border-slate-100">
              <Wallet className="w-10 h-10 text-blue-800" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-1">Pay ₹99</h3>
            <p className="text-blue-800 font-semibold mb-2">₹99 చెల్లించండి</p>
            <p className="text-slate-600">Confirm your booking with just ₹99 visit charge</p>
          </div>
          <div data-testid="step-3" className="relative text-center">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-yellow-400 text-slate-900 font-bold flex items-center justify-center text-sm z-10">3</div>
            <div className="w-24 h-24 mx-auto rounded-full bg-white shadow-lg flex items-center justify-center mb-6 border-4 border-slate-100">
              <CheckCircle2 className="w-10 h-10 text-blue-800" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-1">Get Service</h3>
            <p className="text-blue-800 font-semibold mb-2">సేవ పొందండి</p>
            <p className="text-slate-600">Verified professional arrives at your doorstep</p>
          </div>
        </div>
        <div data-testid="price-transparency-badge" className="mt-16 max-w-2xl mx-auto bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-slate-900 mb-1">100% Price Transparency</h4>
              <p className="text-slate-600">₹99 is only the visit charge. Final service cost is discussed & agreed before work begins. No hidden charges!</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BookingForm({ selectedService, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    service_category: selectedService?.name || "",
    location: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const serviceOptions = [
    "Electrical", "Plumbing", "AC Repair", "Painting", "Pest Control", "Cleaning", "Carpentry", "Masonry",
    "Mobile Repair", "Laptop Repair", "TV Repair", "Inverter Repair", "Appliance Repair", "Washing Machine", "Microwave Repair", "Printer Repair",
    "Drilling", "Wall Mounting", "Lock Repair", "Curtain Rods", "Furniture Assembly", "Door Repair", "Window Repair", "Geyser Install",
    "Documentation", "Real Estate", "Insurance", "Legal Services", "Interior Design", "Architecture", "Vastu Consultation", "Home Inspection"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile || !formData.service_category) {
      toast.error("Please fill all required fields");
      return;
    }
    if (formData.mobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData, preferred_date: date ? format(date, "yyyy-MM-dd") : "ASAP" };
      await axios.post(`${API}/bookings`, payload);
      toast.success("Booking submitted successfully! We'll call you shortly.");
      onClose();
    } catch (error) {
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="name">Full Name *</Label>
        <Input id="name" data-testid="booking-name-input" placeholder="Enter your name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="h-12" required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="mobile">Mobile Number *</Label>
        <Input id="mobile" data-testid="booking-mobile-input" type="tel" placeholder="10-digit mobile number" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="h-12" required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="service">Service Category *</Label>
        <Select value={formData.service_category} onValueChange={(value) => setFormData({ ...formData, service_category: value })}>
          <SelectTrigger data-testid="booking-service-select" className="h-12"><SelectValue placeholder="Select a service" /></SelectTrigger>
          <SelectContent>
            {serviceOptions.map((svc) => (<SelectItem key={svc} value={svc}>{svc}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input id="location" data-testid="booking-location-input" placeholder="Your address" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="h-12" />
      </div>
      <div className="space-y-2">
        <Label>Preferred Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" data-testid="booking-date-picker" className="w-full h-12 justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "PPP") : "Select date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date()} initialFocus />
          </PopoverContent>
        </Popover>
      </div>
      <Button type="submit" data-testid="booking-submit-btn" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold h-14 text-lg rounded-full">
        {loading ? "Submitting..." : "Confirm Booking @ ₹99"}
      </Button>
      <p className="text-center text-sm text-slate-500">₹99 is the visit charge only. Service cost discussed on-site.</p>
    </form>
  );
}

function PartnerSection({ onOpen }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <span></span>
      </DialogTrigger>
    </Dialog>
  );
}

function PartnerFormDialog({ open, onOpenChange }) {
  const [formData, setFormData] = useState({ name: "", mobile: "", service_type: "", experience: "", location: "" });
  const [loading, setLoading] = useState(false);

  const serviceOptions = [
    "Electrical", "Plumbing", "AC Repair", "Painting", "Pest Control", "Cleaning", "Carpentry", "Masonry",
    "Mobile Repair", "Laptop Repair", "TV Repair", "Inverter Repair", "Appliance Repair", "Drilling", 
    "Wall Mounting", "Lock Repair", "Furniture Assembly", "Door Repair", "Geyser Install"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile || !formData.service_type || !formData.location) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/partners`, formData);
      toast.success("Partner registration successful! We'll contact you soon.");
      onOpenChange(false);
      setFormData({ name: "", mobile: "", service_type: "", experience: "", location: "" });
    } catch (error) {
      toast.error("Failed to register. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Join as Service Partner</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Full Name *</Label><Input data-testid="partner-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter your full name" required /></div>
          <div><Label>Mobile Number *</Label><Input data-testid="partner-mobile-input" type="tel" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="10-digit mobile number" required /></div>
          <div><Label>Service Type *</Label>
            <Select value={formData.service_type} onValueChange={(value) => setFormData({ ...formData, service_type: value })}>
              <SelectTrigger data-testid="partner-service-select"><SelectValue placeholder="Select your service" /></SelectTrigger>
              <SelectContent>{serviceOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Experience</Label>
            <Select value={formData.experience} onValueChange={(value) => setFormData({ ...formData, experience: value })}>
              <SelectTrigger data-testid="partner-experience-select"><SelectValue placeholder="Years of experience" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0-1">0-1 Year</SelectItem>
                <SelectItem value="1-3">1-3 Years</SelectItem>
                <SelectItem value="3-5">3-5 Years</SelectItem>
                <SelectItem value="5+">5+ Years</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Service Location(s) *</Label>
            <Input data-testid="partner-location-input" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g., Anantapur, Hindupur, Kadiri" />
            <p className="text-xs text-slate-500 mt-1">Enter areas where you can provide service (comma separated)</p>
          </div>
          <Button type="submit" data-testid="partner-submit-btn" disabled={loading} className="w-full bg-blue-800 hover:bg-blue-900">{loading ? "Submitting..." : "Submit Application"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AffiliateFormDialog({ open, onOpenChange }) {
  const [formData, setFormData] = useState({ name: "", mobile: "", referral_source: "", social_links: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/affiliates`, formData);
      toast.success("Registration successful! We'll contact you soon.");
      onOpenChange(false);
      setFormData({ name: "", mobile: "", referral_source: "", social_links: "" });
    } catch (error) {
      toast.error("Failed to register. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Become a Promotion Partner</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Full Name *</Label><Input data-testid="affiliate-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter your full name" required /></div>
          <div><Label>Mobile Number *</Label><Input data-testid="affiliate-mobile-input" type="tel" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="10-digit mobile number" required /></div>
          <div><Label>How will you promote?</Label>
            <Select value={formData.referral_source} onValueChange={(value) => setFormData({ ...formData, referral_source: value })}>
              <SelectTrigger data-testid="affiliate-source-select"><SelectValue placeholder="Select option" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="social_media">Social Media</SelectItem>
                <SelectItem value="whatsapp">WhatsApp Groups</SelectItem>
                <SelectItem value="local_shop">Local Shop Owner</SelectItem>
                <SelectItem value="word_of_mouth">Word of Mouth</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Social Media Links (Optional)</Label><Input data-testid="affiliate-social-input" placeholder="Instagram, Facebook, etc." value={formData.social_links} onChange={(e) => setFormData({ ...formData, social_links: e.target.value })} /></div>
          <Button type="submit" data-testid="affiliate-submit-btn" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900">{loading ? "Submitting..." : "Join as Affiliate"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Footer() {
  const [termsOpen, setTermsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-1 mb-4">
              <span className="text-3xl font-extrabold text-blue-400">99</span>
              <span className="text-2xl font-bold text-white">Home</span>
              <span className="text-2xl font-bold text-yellow-400">Deals</span>
            </div>
            <p className="text-slate-400 mb-4 max-w-sm">Your trusted platform for home services in Anantapur. Quality service at affordable prices with just ₹99 booking.</p>
            <div className="flex items-center gap-2 text-slate-400"><MapPin className="w-4 h-4" /><span>Anantapur, Andhra Pradesh - 515002</span></div>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><a href="#services" className="text-slate-400 hover:text-white transition-colors">Services</a></li>
              <li><a href="#partner" className="text-slate-400 hover:text-white transition-colors">Join as Partner</a></li>
              <li><a href="#affiliate" className="text-slate-400 hover:text-white transition-colors">Earn with Us</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li><a href={`tel:${PHONE_NUMBER}`} data-testid="footer-phone-link" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"><Phone className="w-4 h-4" />+91 95022 99229</a></li>
              <li><a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="flex items-center gap-2 text-slate-400 hover:text-green-400 transition-colors"><MessageCircle className="w-4 h-4" />WhatsApp Support</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">© 2024 99Homedeals.in. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm">
            <button onClick={() => setTermsOpen(true)} data-testid="terms-link" className="text-slate-400 hover:text-white transition-colors">Terms & Conditions (₹99 Policy)</button>
            <button onClick={() => setAboutOpen(true)} data-testid="about-link" className="text-slate-400 hover:text-white transition-colors">About Us</button>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Dialog */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Terms & Conditions - ₹99 Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-slate-600 mt-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-bold text-slate-900 mb-2">₹99 Visit Charge Policy</h3>
              <p>The ₹99 is a <strong>confirmation/visit charge only</strong>. This amount is collected to confirm your booking and ensure a verified professional visits your location.</p>
            </div>
            
            <div>
              <h3 className="font-bold text-slate-900 mb-2">1. Service Charges</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>₹99 is the visit/inspection charge, not the total service cost</li>
                <li>Actual service charges will be discussed and agreed upon by the customer and service provider before work begins</li>
                <li>Service charges vary based on the nature and complexity of the work</li>
                <li>No work will begin without customer's explicit consent on the final price</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 mb-2">2. Booking & Cancellation</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Bookings can be cancelled up to 2 hours before the scheduled visit for a full refund</li>
                <li>Late cancellations may be subject to a cancellation fee</li>
                <li>No-shows by the customer will result in forfeiture of the ₹99 visit charge</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 mb-2">3. Service Quality</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>All service providers are verified and background-checked</li>
                <li>7-day warranty on workmanship for eligible services</li>
                <li>Customer feedback is taken seriously for quality improvement</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 mb-2">4. Payment Terms</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>₹99 visit charge is payable at the time of booking</li>
                <li>Service charges are payable directly to the service provider upon completion</li>
                <li>We accept UPI, cash, and digital payments</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 mb-2">5. Liability</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>99Homedeals acts as a platform connecting customers with service providers</li>
                <li>Service providers are independent contractors, not employees</li>
                <li>Any disputes should be reported within 24 hours of service completion</li>
              </ul>
            </div>

            <p className="text-sm text-slate-500 pt-4 border-t">Last updated: January 2024. For queries, contact us on WhatsApp: +91 95022 99229</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* About Us Dialog */}
      <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">About 99Homedeals</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-slate-600 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-lg">మీ ఇంటి పనులన్నీ – ఒకే ప్లాట్‌ఫామ్‌లో</p>
              <p className="text-slate-700 font-medium mt-1">All your home service needs - on one platform!</p>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 mb-2">Our Mission</h3>
              <p>99Homedeals is Anantapur's trusted home services platform, connecting homeowners with skilled, verified professionals for all their repair, maintenance, and home improvement needs. We believe quality home services should be accessible, affordable, and hassle-free.</p>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 mb-2">Why Choose Us?</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Affordable:</strong> Book any service starting at just ₹99</li>
                <li><strong>Verified Professionals:</strong> All service providers are background-verified</li>
                <li><strong>Transparent Pricing:</strong> No hidden charges - price agreed before work begins</li>
                <li><strong>Wide Range:</strong> 30+ services across Home Care, Tech Repairs, Handyman & Pro Services</li>
                <li><strong>Local Focus:</strong> Proudly serving Anantapur and surrounding areas</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 mb-2">Our Services</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-slate-50 p-2 rounded">Electrical & Plumbing</div>
                <div className="bg-slate-50 p-2 rounded">AC Repair & Service</div>
                <div className="bg-slate-50 p-2 rounded">Painting & Carpentry</div>
                <div className="bg-slate-50 p-2 rounded">Mobile & Laptop Repair</div>
                <div className="bg-slate-50 p-2 rounded">TV & Appliance Repair</div>
                <div className="bg-slate-50 p-2 rounded">Home Cleaning</div>
                <div className="bg-slate-50 p-2 rounded">Pest Control</div>
                <div className="bg-slate-50 p-2 rounded">Documentation & Legal</div>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 mb-2">Contact Us</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-blue-600" /> +91 95022 99229</li>
                <li className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-green-600" /> WhatsApp: 9502299229</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-red-600" /> Anantapur, Andhra Pradesh - 515002</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="font-bold text-slate-900">Join Our Network!</p>
              <p className="text-sm">Are you a skilled professional? <a href="#partner" onClick={() => setAboutOpen(false)} className="text-blue-600 underline">Register as a Partner</a></p>
              <p className="text-sm">Want to earn? <a href="#affiliate" onClick={() => setAboutOpen(false)} className="text-blue-600 underline">Become an Affiliate</a></p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}

function FloatingButtons() {
  return (
    <>
      <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I need a home service`} target="_blank" rel="noopener noreferrer" data-testid="floating-whatsapp-btn" className="whatsapp-btn w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110">
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
      <a href={`tel:${PHONE_NUMBER}`} data-testid="floating-call-btn" className="floating-call-btn w-14 h-14 bg-yellow-400 hover:bg-yellow-500 rounded-full flex items-center justify-center shadow-lg">
        <Phone className="w-7 h-7 text-slate-900" />
      </a>
    </>
  );
}

function AdminDashboard({ onClose }) {
  const [activeTab, setActiveTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, partnersRes, affiliatesRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/partners`),
        axios.get(`${API}/affiliates`)
      ]);
      setBookings(bookingsRes.data);
      setPartners(partnersRes.data);
      setAffiliates(affiliatesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status });
      setBookings(bookings.map(b => b.id === id ? { ...b, status } : b));
      toast.success(`Booking ${status}`);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const updatePartnerStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/partners/${id}`, { status });
      setPartners(partners.map(p => p.id === id ? { ...p, status } : p));
      toast.success(`Partner ${status}`);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-green-100 text-green-800",
      completed: "bg-blue-100 text-blue-800",
      cancelled: "bg-red-100 text-red-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800"
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || colors.pending}`}>{status}</span>;
  };

  // Get services available by location (based on approved partners)
  const getServicesByLocation = () => {
    const locationServices = {};
    partners.filter(p => p.status === "approved").forEach(p => {
      const locs = p.location.includes("All Places") ? LOCATIONS.filter(l => l !== "All Places") : p.location.split(", ");
      locs.forEach(loc => {
        if (!locationServices[loc]) locationServices[loc] = new Set();
        locationServices[loc].add(p.service_type);
      });
    });
    return locationServices;
  };

  const locationServices = getServicesByLocation();

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-blue-800 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Admin Dashboard</h2>
            <p className="text-blue-200 text-sm">Manage bookings, partners & affiliates</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-blue-700 rounded-lg transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 p-6 bg-slate-50 border-b">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-slate-500 text-sm">Total Bookings</p>
            <p className="text-3xl font-bold text-slate-900">{bookings.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-slate-500 text-sm">Pending Bookings</p>
            <p className="text-3xl font-bold text-yellow-600">{bookings.filter(b => b.status === "pending").length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-slate-500 text-sm">Active Partners</p>
            <p className="text-3xl font-bold text-green-600">{partners.filter(p => p.status === "approved").length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-slate-500 text-sm">Affiliates</p>
            <p className="text-3xl font-bold text-blue-600">{affiliates.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-white">
          {[
            { key: "bookings", label: "Bookings", count: bookings.length },
            { key: "partners", label: "Partners", count: partners.length },
            { key: "affiliates", label: "Affiliates", count: affiliates.length },
            { key: "locations", label: "Service Coverage", count: Object.keys(locationServices).length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-4 font-semibold text-sm transition-colors relative ${activeTab === tab.key ? "text-blue-800" : "text-slate-500 hover:text-slate-700"}`}
            >
              {tab.label} ({tab.count})
              {activeTab === tab.key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-800" />}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-4 border-blue-800 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <>
              {/* Bookings Tab */}
              {activeTab === "bookings" && (
                <div className="space-y-4">
                  {bookings.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No bookings yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Customer</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Mobile</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Service</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Location</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Date</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Status</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bookings.map((booking) => (
                            <tr key={booking.id} className="hover:bg-slate-50">
                              <td className="p-3 font-medium text-slate-900">{booking.name}</td>
                              <td className="p-3"><a href={`tel:${booking.mobile}`} className="text-blue-600 hover:underline">{booking.mobile}</a></td>
                              <td className="p-3">{booking.service_category}</td>
                              <td className="p-3 text-sm text-slate-600">{booking.location}</td>
                              <td className="p-3 text-sm">{booking.preferred_date}</td>
                              <td className="p-3">{getStatusBadge(booking.status)}</td>
                              <td className="p-3">
                                <div className="flex gap-2">
                                  <button onClick={() => updateBookingStatus(booking.id, "confirmed")} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">Confirm</button>
                                  <button onClick={() => updateBookingStatus(booking.id, "completed")} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Complete</button>
                                  <button onClick={() => updateBookingStatus(booking.id, "cancelled")} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">Cancel</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Partners Tab */}
              {activeTab === "partners" && (
                <div className="space-y-4">
                  {partners.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No partners registered yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Name</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Mobile</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Service</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Experience</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Locations</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Status</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {partners.map((partner) => (
                            <tr key={partner.id} className="hover:bg-slate-50">
                              <td className="p-3 font-medium text-slate-900">{partner.name}</td>
                              <td className="p-3"><a href={`tel:${partner.mobile}`} className="text-blue-600 hover:underline">{partner.mobile}</a></td>
                              <td className="p-3">{partner.service_type}</td>
                              <td className="p-3">{partner.experience || "-"}</td>
                              <td className="p-3 text-sm text-slate-600 max-w-xs truncate">{partner.location}</td>
                              <td className="p-3">{getStatusBadge(partner.status)}</td>
                              <td className="p-3">
                                <div className="flex gap-2">
                                  <button onClick={() => updatePartnerStatus(partner.id, "approved")} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">Approve</button>
                                  <button onClick={() => updatePartnerStatus(partner.id, "rejected")} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">Reject</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Affiliates Tab */}
              {activeTab === "affiliates" && (
                <div className="space-y-4">
                  {affiliates.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No affiliates registered yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Name</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Mobile</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Promotion Method</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Social Links</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-600">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {affiliates.map((affiliate) => (
                            <tr key={affiliate.id} className="hover:bg-slate-50">
                              <td className="p-3 font-medium text-slate-900">{affiliate.name}</td>
                              <td className="p-3"><a href={`tel:${affiliate.mobile}`} className="text-blue-600 hover:underline">{affiliate.mobile}</a></td>
                              <td className="p-3">{affiliate.referral_source || "-"}</td>
                              <td className="p-3 text-sm text-slate-600">{affiliate.social_links || "-"}</td>
                              <td className="p-3">{getStatusBadge(affiliate.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Service Coverage Tab */}
              {activeTab === "locations" && (
                <div className="space-y-4">
                  <p className="text-slate-600 mb-4">Services available by location (based on approved partners)</p>
                  {Object.keys(locationServices).length === 0 ? (
                    <div className="text-center py-8 bg-yellow-50 rounded-xl border border-yellow-200">
                      <p className="text-yellow-800 font-medium">No approved partners yet</p>
                      <p className="text-yellow-600 text-sm mt-1">Approve partners to see service coverage</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(locationServices).map(([location, services]) => (
                        <div key={location} className="bg-white border rounded-xl p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-2 mb-3">
                            <MapPin className="w-5 h-5 text-blue-800" />
                            <h3 className="font-bold text-slate-900">{location}</h3>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {Array.from(services).map(service => (
                              <span key={service} className="px-2 py-1 bg-blue-50 text-blue-800 text-xs rounded-full">{service}</span>
                            ))}
                          </div>
                          <p className="text-xs text-slate-500 mt-3">{services.size} service(s) available</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [adminOpen, setAdminOpen] = useState(false);

  const handleBookNow = () => {
    setSelectedService(null);
    setBookingOpen(true);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setBookingOpen(true);
  };

  return (
    <div className="App min-h-screen">
      <Toaster position="top-center" richColors />
      <Header onAdminClick={() => setAdminOpen(true)} />
      <main>
        <HeroSection onBookNow={handleBookNow} />
        <ServicesSection onServiceSelect={handleServiceSelect} />
        <HowItWorksSection />
        <PartnerSection />
      </main>
      <Footer />
      <FloatingButtons />
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-xl">Book Service</DialogTitle></DialogHeader>
          <BookingForm selectedService={selectedService} onClose={() => setBookingOpen(false)} />
        </DialogContent>
      </Dialog>
      {adminOpen && <AdminDashboard onClose={() => setAdminOpen(false)} />}
    </div>
  );
}

export default App;
